import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import { Translate, I18n } from 'react-redux-i18n';
import { buildPayoffSeries } from 'utils/optionPayoff';

function formatMoney(value) {
    if (!Number.isFinite(value)) {
        return '—';
    }
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}`;
}

function daysToExpiration(expireAt) {
    if (!expireAt) {
        return null;
    }
    const exp = new Date(expireAt);
    if (Number.isNaN(exp.getTime())) {
        return null;
    }
    const todayUtc = Date.UTC(
        new Date().getUTCFullYear(),
        new Date().getUTCMonth(),
        new Date().getUTCDate(),
        0, 0, 0,
    );
    const expUtc = Date.UTC(
        exp.getUTCFullYear(),
        exp.getUTCMonth(),
        exp.getUTCDate(),
        0, 0, 0,
    );
    return Math.trunc((expUtc - todayUtc) / (1000 * 60 * 60 * 24));
}

function priceToIndex(labels, price) {
    let nearestIdx = 0;
    let best = Infinity;
    (labels || []).forEach((label, idx) => {
        const diff = Math.abs(Number(label) - Number(price));
        if (diff < best) {
            best = diff;
            nearestIdx = idx;
        }
    });
    return nearestIdx;
}

function drawMarkerOnZero(ctx, x, y0, chartArea, {
    color,
    label,
    labelAbove = true,
}) {
    if (x < chartArea.left || x > chartArea.right) {
        return;
    }
    if (y0 < chartArea.top || y0 > chartArea.bottom) {
        return;
    }

    ctx.save();

    // Vertical guide through the marker
    ctx.beginPath();
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 1.25;
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    // Point on the zero line
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.arc(x, y0, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Inner highlight
    ctx.beginPath();
    ctx.fillStyle = '#fff';
    ctx.arc(x, y0, 2.2, 0, Math.PI * 2);
    ctx.fill();

    // Label
    ctx.font = '600 11px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = labelAbove ? 'bottom' : 'top';
    const labelY = labelAbove ? y0 - 10 : y0 + 10;
    const metrics = ctx.measureText(label);
    const padX = 6;
    const padY = 3;
    const boxW = metrics.width + padX * 2;
    const boxH = 16;
    const boxX = x - boxW / 2;
    const boxY = labelAbove ? labelY - boxH : labelY;

    ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(boxX, boxY, boxW, boxH, 4);
    } else {
        ctx.rect(boxX, boxY, boxW, boxH);
    }
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = color;
    ctx.fillText(label, x, labelAbove ? boxY + boxH - padY : boxY + padY);

    ctx.restore();
}

const zeroLinePlugin = {
    afterDraw(chart) {
        const { ctx, chartArea } = chart;
        if (!chartArea) {
            return;
        }
        const yScale = chart.scales['y-axis-0'] || chart.scales.y;
        const xScale = chart.scales['x-axis-0'] || chart.scales.x;
        if (!yScale || !xScale) {
            return;
        }

        const y0 = yScale.getPixelForValue(0);
        if (y0 >= chartArea.top && y0 <= chartArea.bottom) {
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([6, 4]);
            ctx.strokeStyle = 'rgba(148, 163, 184, 0.7)';
            ctx.lineWidth = 1;
            ctx.moveTo(chartArea.left, y0);
            ctx.lineTo(chartArea.right, y0);
            ctx.stroke();
            ctx.restore();
        }

        const labels = chart.data.labels || [];
        const opts = chart.options || {};
        const spot = Number(opts._ebiSpot);
        const strikes = Array.isArray(opts._ebiStrikes) ? opts._ebiStrikes : [];

        strikes.forEach((strike, idx) => {
            const s = Number(strike);
            if (!Number.isFinite(s) || s <= 0) {
                return;
            }
            const x = xScale.getPixelForValue(priceToIndex(labels, s));
            drawMarkerOnZero(ctx, x, y0, chartArea, {
                color: '#fbbf24',
                label: `K $${s.toFixed(2)}`,
                labelAbove: idx % 2 === 0,
            });
        });

        if (Number.isFinite(spot) && spot > 0) {
            const x = xScale.getPixelForValue(priceToIndex(labels, spot));
            drawMarkerOnZero(ctx, x, y0, chartArea, {
                color: '#38bdf8',
                label: `Spot $${spot.toFixed(2)}`,
                labelAbove: true,
            });
        }
    },
};

const OptionPayoffChart = ({ position, stockPrice, metrics }) => {
    const series = useMemo(
        () => buildPayoffSeries(position, stockPrice),
        [position, stockPrice],
    );

    const chartData = useMemo(() => {
        const profit = series.values.map((v) => (v >= 0 ? v : null));
        const loss = series.values.map((v) => (v < 0 ? v : null));
        return {
            labels: series.labels,
            datasets: [
                {
                    label: I18n.t('analysis_profit_zone'),
                    data: profit,
                    borderColor: '#22c55e',
                    backgroundColor: 'rgba(34, 197, 94, 0.22)',
                    pointRadius: 0,
                    borderWidth: 2,
                    fill: 'origin',
                    lineTension: 0,
                    spanGaps: false,
                },
                {
                    label: I18n.t('analysis_loss_zone'),
                    data: loss,
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.22)',
                    pointRadius: 0,
                    borderWidth: 2,
                    fill: 'origin',
                    lineTension: 0,
                    spanGaps: false,
                },
            ],
        };
    }, [series]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            labels: { fontColor: '#cbd5e1' },
        },
        tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
                title(items) {
                    if (!items?.length) return '';
                    return `${I18n.t('analysis_expire_price')}: $${items[0].label}`;
                },
                label(item) {
                    const v = Number(item.value);
                    if (!Number.isFinite(v)) return null;
                    return `P/L: $${formatMoney(v)}`;
                },
            },
        },
        scales: {
            xAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: I18n.t('analysis_expire_price'),
                    fontColor: '#94a3b8',
                },
                ticks: {
                    fontColor: '#94a3b8',
                    maxTicksLimit: 10,
                    callback(value) {
                        return `$${value}`;
                    },
                },
                gridLines: { color: 'rgba(148, 163, 184, 0.12)' },
            }],
            yAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: 'P/L $',
                    fontColor: '#94a3b8',
                },
                ticks: {
                    fontColor: '#94a3b8',
                    callback(value) {
                        return `$${value}`;
                    },
                },
                gridLines: { color: 'rgba(148, 163, 184, 0.12)' },
            }],
        },
        _ebiSpot: series.spot,
        _ebiStrikes: series.strikes || [],
    }), [series.spot, series.strikes]);

    return (
        <div className="analysis-chart-wrap">
            <div className="analysis-stats">
                <div className="analysis-stat">
                    <span className="analysis-stat-label">
                        <Translate value="analysis_current_price" />
                    </span>
                    <span className="analysis-stat-value">
                        ${Number(series.spot || 0).toFixed(2)}
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">
                        <Translate value="analysis_contract_current" />
                    </span>
                    <span className="analysis-stat-value">
                        {Array.isArray(metrics?.prices) && metrics.prices.length
                            ? (metrics.prices.length === 1
                                ? `$${Number(metrics.prices[0] || 0).toFixed(2)}`
                                : metrics.prices.map((price) => `$${Number(price || 0).toFixed(2)}`).join(' / '))
                            : '—'}
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">NetLiq</span>
                    <span className="analysis-stat-value">
                        ${Number(metrics?.netLiq || 0).toFixed(2)}
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">P/L</span>
                    <span className={`analysis-stat-value ${Number(metrics?.pl || 0) >= 0 ? 'is-profit' : 'is-loss'}`}>
                        ${formatMoney(Number(metrics?.pl || 0))}
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">P/L %</span>
                    <span className={`analysis-stat-value ${Number(metrics?.plPercent || 0) >= 0 ? 'is-profit' : 'is-loss'}`}>
                        {Number(metrics?.plPercent || 0).toFixed(2)}%
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">
                        <Translate value="analysis_max_profit" />
                    </span>
                    <span className="analysis-stat-value is-profit">
                        ${formatMoney(series.maxProfit)}
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">
                        <Translate value="analysis_max_loss" />
                    </span>
                    <span className="analysis-stat-value is-loss">
                        ${formatMoney(series.maxLoss)}
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">
                        <Translate value="analysis_breakeven" />
                    </span>
                    <span className="analysis-stat-value">
                        {series.breakevens.length
                            ? series.breakevens.map((b) => `$${b}`).join(' · ')
                            : '—'}
                    </span>
                </div>
                <div className="analysis-stat">
                    <span className="analysis-stat-label">
                        <Translate value="analysis_dte" />
                    </span>
                    <span className="analysis-stat-value">
                        {(() => {
                            const expireAt = position.expire_at
                                || (Array.isArray(position.sub_order) && position.sub_order[0]?.expire_at);
                            const dte = daysToExpiration(expireAt);
                            if (dte == null) {
                                return '—';
                            }
                            return dte;
                        })()}
                    </span>
                </div>
            </div>
            <div className="analysis-chart-canvas">
                <Line data={chartData} options={options} plugins={[zeroLinePlugin]} />
            </div>
            <p className="analysis-chart-hint">
                <Translate value="analysis_chart_hint" />
            </p>
        </div>
    );
};

OptionPayoffChart.propTypes = {
    position: PropTypes.object.isRequired,
    stockPrice: PropTypes.number,
    metrics: PropTypes.shape({
        prices: PropTypes.arrayOf(PropTypes.number),
        netLiq: PropTypes.number,
        pl: PropTypes.number,
        plPercent: PropTypes.number,
    }),
};

export default OptionPayoffChart;
